export type SentryRuntime = "api" | "next-server" | "next-edge" | "next-client"

export type SentryConfigInput = {
  env: Record<string, string | undefined>
  runtime: SentryRuntime
  serviceName: string
  defaultTracesSampleRate?: number
  defaultProfilesSampleRate?: number
}

export type ResolvedSentryConfig = {
  enabled: boolean
  runtime: SentryRuntime
  serviceName: string
  dsn?: string
  environment: string
  release?: string
  tracesSampleRate: number
  profilesSampleRate: number
  debug: boolean
  sendDefaultPii: boolean
  tunnelRoute?: string
  sourceMaps: {
    authToken?: string
    org?: string
    project?: string
  }
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined
  }

  const normalized = value.trim()
  return normalized.length > 0 ? normalized : undefined
}

function parseSampleRate(value: string | undefined, fallback: number): number {
  const normalized = normalizeOptionalString(value)
  if (!normalized) {
    return fallback
  }

  const parsed = Number(normalized)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback
}

function parseBoolean(value: string | undefined): boolean {
  const normalized = normalizeOptionalString(value)
  return normalized === "1" || normalized === "true"
}

export function resolveSentryConfig(input: SentryConfigInput): ResolvedSentryConfig {
  const { env, runtime, serviceName } = input
  const isBrowserRuntime = runtime === "next-client"
  const dsn = isBrowserRuntime
    ? normalizeOptionalString(env.NEXT_PUBLIC_SENTRY_DSN) || normalizeOptionalString(env.SENTRY_DSN)
    : normalizeOptionalString(env.SENTRY_DSN) || normalizeOptionalString(env.NEXT_PUBLIC_SENTRY_DSN)
  const tracesSampleRate = parseSampleRate(
    env.SENTRY_TRACES_SAMPLE_RATE || (isBrowserRuntime ? env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE : undefined),
    input.defaultTracesSampleRate ?? 0.1
  )

  return {
    enabled: Boolean(dsn),
    runtime,
    serviceName,
    dsn,
    environment:
      normalizeOptionalString(env.SENTRY_ENVIRONMENT) ||
      normalizeOptionalString(env.VERCEL_ENV) ||
      normalizeOptionalString(env.ENVIRONMENT) ||
      normalizeOptionalString(env.NODE_ENV) ||
      "development",
    release:
      normalizeOptionalString(env.SENTRY_RELEASE) ||
      normalizeOptionalString(env.VERCEL_GIT_COMMIT_SHA) ||
      normalizeOptionalString(env.SOURCE_VERSION),
    tracesSampleRate,
    profilesSampleRate: parseSampleRate(
      env.SENTRY_PROFILES_SAMPLE_RATE || (isBrowserRuntime ? env.NEXT_PUBLIC_SENTRY_PROFILES_SAMPLE_RATE : undefined),
      input.defaultProfilesSampleRate ?? 0
    ),
    debug: parseBoolean(env.SENTRY_DEBUG),
    sendDefaultPii: true,
    tunnelRoute: normalizeOptionalString(env.NEXT_PUBLIC_SENTRY_TUNNEL_ROUTE),
    sourceMaps: {
      authToken: normalizeOptionalString(env.SENTRY_AUTH_TOKEN),
      org: normalizeOptionalString(env.SENTRY_ORG),
      project: normalizeOptionalString(env.SENTRY_PROJECT),
    },
  }
}
